import React, { useCallback, useRef } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { cn } from "@/lib/utils";
import { handleFileUpload } from '@/utils/files/upload';
import { UPLOAD_CONFIG } from '@/config';

interface FileUploadProps {
  value: string;
  onChange: (value: string) => void;
  type: 'image' | 'pdf';
  label: string;
  onError?: (error: string) => void;
  previewUrl?: string;
}

export function FileUpload({ 
  value, 
  onChange, 
  type,
  label,
  onError,
  previewUrl
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const maxSize = type === 'image' ? UPLOAD_CONFIG.MAX_IMAGE_SIZE : UPLOAD_CONFIG.MAX_PDF_SIZE;
  const accept = type === 'image' ? UPLOAD_CONFIG.ACCEPTED_IMAGE_TYPES.join(',') : UPLOAD_CONFIG.ACCEPTED_PDF_TYPES.join(',');

  const processFile = async (file: File) => {
    const base64 = await handleFileUpload(file, maxSize, onError);
    if (base64) {
      onChange(base64);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  }, []);

  return (
    <div className="space-y-2">
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer bg-white",
          "border-gray-200 hover:border-primary/50",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        )}
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <Upload className="h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-500 text-center">
            اسحب وأفلت {label} هنا، أو انقر للاختيار
          </p>
          <p className="text-xs text-gray-400">
            الحد الأقصى للحجم: {Math.round(maxSize / (1024 * 1024))}MB
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileInput}
            className="hidden"
          />
        </div>
      </div>

      {value && (
        <div className="relative">
          {type === 'image' ? (
            <div className="relative w-20 h-20 rounded-lg overflow-hidden">
              <img 
                src={previewUrl || value}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute top-1 right-1 p-1 bg-white rounded-full shadow-sm hover:bg-gray-100"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <FileText className="h-5 w-5 text-gray-500" />
              <div className="flex-1 truncate text-sm">تم اختيار الملف</div>
              <button
                type="button"
                onClick={() => onChange('')}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}